import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import { type UseFieldArrayReturn, useFormContext } from "react-hook-form";
import { useIsOnline } from "src/hooks";
import { type IFgasForm } from "src/pages/Settings/YachtSetup/Fgas/Fgas.types";
import { gwpFields } from "src/pages/Settings/YachtSetup/Fgas/Fgas.utils";

import styles from "../YachtSetUp.module.scss";
import { StyledYachtSetUpItem } from "../YachtSetUp.styled";
import FgasGwpTableHead from "./FgasGwpTableHead";
import FgasHistory from "./FgasHistory";

type Props = Pick<UseFieldArrayReturn<IFgasForm, "gwp">, "fields"> & {
  onAdd: () => void;
  onRemove: (index: number) => void;
};

const FgasGwpList = ({ fields, onAdd, onRemove }: Props) => {
  const { register } = useFormContext<IFgasForm>();
  const isOnline = useIsOnline();

  return (
    <Flex
      direction="column"
      flex={1}
      miw={0}
      w="100%"
      style={{ alignSelf: "stretch" }}>
      <Flex w="100%" mih={36} justify="space-between" align="center">
        <Title order={3} className={styles.sectionTitle}>
          GWP
        </Title>
        <Group gap="sm">
          <FgasHistory type="gwp" />
          {isOnline && (
            <Button
              onClick={onAdd}
              variant="outline"
              leftSection={<IconPlus size="1.4rem" />}>
              Add
            </Button>
          )}
        </Group>
      </Flex>
      <StyledYachtSetUpItem
        style={{
          width: "100%",
          padding: 0,
          flex: "0 1 auto",
          minHeight: 0,
          overflow: "hidden",
          alignItems: "stretch",
        }}>
        <Table.ScrollContainer
          type="native"
          minWidth={700}
          w="100%"
          style={{ overflowY: "auto" }}>
          <Table
            stickyHeader
            className={styles.stickyBorderTable}
            withColumnBorders
            verticalSpacing="xs"
            borderColor="var(--mantine-color-settingsColor-0)">
            <FgasGwpTableHead withActions={isOnline} />
            <Table.Tbody>
              {fields.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={gwpFields.length + 1}>
                    <Text c="dimmed" ta="center" size="sm">
                      No GWP items added.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {fields.map((item, index) => (
                <Table.Tr key={item.id}>
                  {gwpFields.map(field => (
                    <Table.Td key={field.name}>
                      <TextInput
                        size="sm"
                        disabled={!isOnline}
                        aria-label={field.label}
                        {...register(`gwp.${index}.${field.name}`)}
                      />
                    </Table.Td>
                  ))}
                  {isOnline && (
                    <Table.Td>
                      <Flex justify="center" align="center">
                        <ActionIcon
                          className={styles.styledRemoveActionIcon}
                          onClick={() => onRemove(index)}
                          variant="outline"
                          size="sm"
                          aria-label="Remove">
                          <IconX />
                        </ActionIcon>
                      </Flex>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </StyledYachtSetUpItem>
    </Flex>
  );
};

export default FgasGwpList;
